// React adapter for the existing invoice editor. Calculations and persistence stay
// in its established backend facade; this bridge only edits supported form fields.
function useInvoiceControlEditor(s, { mode, setMode, setStep, setShowPreview, blocked, onIssued, onSent }) {
  const latest = useRef(null);
  const businessId = window.PlummeBackend.activeBusinessId;
  latest.current = { s, mode, blocked, onIssued, onSent };
  const editorYear = useRef(window.PlummeBackend.appState?.activeYear);
  const actions = useRef(null);
  if (!actions.current) actions.current = window.PlummeInvoiceLiveActions.create({
    businessId, backend: () => window.PlummeBackend, editor: () => latest.current.s,
    blocked: () => latest.current.blocked, isCurrent: () => window.PlummeBackend.activeBusinessId === businessId && window.PlummeBackend.appState?.activeYear === editorYear.current,
    records: (kind) => kind === 'customer' ? readCustomers() : readProducts(), productAccount: productDefaultSalesAccount,
    onIssued: (invoice) => latest.current.onIssued?.(invoice),
    onSent: (invoice) => latest.current.onSent?.(invoice),
    deliveryPending: (key) => localStorage.getItem(key) === 'pending',
    markDeliveryPending: (key) => localStorage.setItem(key, 'pending'),
  });
  useEffect(() => window.PlummeInvoiceControl.registerEditor(businessId, {
    version: 2,
    liveVersion: 1,
    saveRecord: (...args) => actions.current.saveRecord(...args),
    fulfill: (step) => actions.current.fulfill(step),
    perform: (step) => window.PlummeInvoiceLiveEditor.perform({
      editor: () => latest.current.s, layout: () => latest.current.mode, blocked: () => latest.current.blocked,
      customers: readCustomers, products: readProducts, productPatch: invoiceLinePatchFromProduct,
      vatOptions: () => window.PlummeBackend.getSalesVatOptions(),
      present: (section) => {
        if (latest.current.mode === 'steps') setStep({ customer: 0, details: 1, lines: 2 }[section]);
        else setMode('onepage');
        setShowPreview(true);
      },
    }, step),
    snapshot: () => {
      const editor = latest.current.s;
      return {
        key: window.PlummeBackend.editorDraftKey(editor),
        dirty: window.PlummeBackend.editorDraftKey(editor) !== editor.savedDraftKey,
        layout: latest.current.mode,
        ui: window.PlummeInvoiceLiveEditor.snapshot(editor, latest.current.mode, latest.current.blocked),
        hasContent: Boolean(editor.customer || editor.lines.some((line) => String(line.desc || '').trim())),
        payload: { action: 'create_invoice', normalizedInvoice: {
          customerId: editor.customer?.id || '', customerName: editor.customer?.name || '',
          invoiceDate: editor.invDate, dueDate: editor.dueDate,
          comment: editor.comment || '',
          lines: editor.lines.filter((line) => String(line.desc || '').trim()).map((line) => ({
            description: line.desc, quantity: Number(line.qty),
            unitPrice: window.PlummeMoneyInput.number(line.price, 0),
            vatRate: window.PlummeBackend.normalizeSalesVatOption(line.vat).rate,
          })), missingFields: [],
        } },
      };
    },
    apply: (invoice, { partial = false } = {}) => {
      const editor = latest.current.s;
      if (partial) {
        if (latest.current.blocked || editor.docType !== 'faktura' || editor.repeating || editor.customer?.isList || (editor.sourceDraftNum && !editor.sourceDraftId)) throw new Error('control.advancedInvoice');
        if (!invoice?.customerId) return false;
        const customer = readCustomers().find((entry) => entry.id === invoice.customerId);
        if (!customer) throw new Error('control.customerRequired');
        editor.setCustomer(customer); editor.setCustEnabled(true);
        return true;
      }
      if (latest.current.blocked || editor.docType !== 'faktura' || editor.repeating || editor.customer?.isList || (editor.sourceDraftNum && !editor.sourceDraftId) || editor.lines.some((line) =>
        (line.lineType && line.lineType !== 'product') || Number(line.disc) || line.rentalPricing?.enabled || line.note || line.salesAccountCode || line.productNumber || line.itemNumber || line.unit || line.subStart || line.rentalFrom)) {
        throw new Error('control.advancedInvoice');
      }
      if (!invoice || !Array.isArray(invoice.lines) || !invoice.lines.length) throw new Error('control.failed');
      const customer = readCustomers().find((entry) => entry.id === invoice.customerId);
      if (!customer) throw new Error('control.customerRequired');
      const vatOptions = window.PlummeBackend.getSalesVatOptions();
      const lines = invoice.lines.map((line, index) => {
        const previous = editor.lines[index];
        const candidates = vatOptions.filter((option) => Number(window.PlummeBackend.normalizeSalesVatOption(option.value).rate) === Number(line.vatRate));
        const previousOption = previous && String(previous.desc || '').trim() && candidates.find((option) => option.value === previous.vat);
        if (!previousOption && candidates.length !== 1) throw new Error('control.advancedInvoice');
        return { ...(previous || {}), id: previous?.id || crypto.randomUUID(), lineType: 'product',
          desc: line.description, qty: line.quantity, price: line.unitPrice, vat: (previousOption || candidates[0]).value, disc: 0 };
      });
      editor.setCustomer(customer);
      editor.setCustEnabled(true);
      editor.setInvDate(invoice.invoiceDate);
      editor.setDueDate(invoice.dueDate);
      if (typeof invoice.comment === 'string') editor.setComment(invoice.comment);
      editor.setLines(lines);
      if (latest.current.mode === 'steps') setStep(2);
      else if (latest.current.mode !== 'onepage') setMode('onepage');
      setShowPreview(true);
      return true;
    },
    setLayout: (layout) => {
      if (!['onepage', 'steps'].includes(layout)) throw new Error('control.invoiceOnly');
      setMode(layout);
    },
    review: () => { latest.current.s.setPreviewAtt?.(null); setStep(3); setShowPreview(true); },
    save: async () => {
      const editor = latest.current.s;
      if (window.PlummeBackend.activeBusinessId !== businessId || latest.current.blocked || !['faktura', 'tilbud', 'ordre'].includes(editor.docType)) throw new Error('control.editorUnavailable');
      if (!editor.customer || !editor.lines.some((line) => String(line.desc || '').trim())) throw new Error('control.incomplete');
      const key = window.PlummeBackend.editorDraftKey(editor);
      if (editor.sourceDraftId && key === editor.savedDraftKey) return;
      const invoice = window.PlummeBackend.buildInvoiceFromEditor(editor, 'draft');
      // Reserve the ID without a saved fingerprint. A retry after an uncertain
      // network result must address the same document, including from the editor.
      if (!editor.sourceDraftId) editor.markDraftSaved(invoice.id, '');
      const withAttachments = await window.PlummeBackend.uploadEditorAttachments(invoice, editor.attachments, 'invoices');
      if (window.PlummeBackend.activeBusinessId !== businessId) throw new Error('control.editorUnavailable');
      const saved = await window.PlummeBackend.upsertInvoice(withAttachments);
      if (!saved?.id) throw new Error('control.failed');
      editor.markDraftSaved(saved.id, key);
    },
  }), [businessId]);
}
Object.assign(globalThis, { useInvoiceControlEditor });
